import React from "react";

import cx from "classnames";

import styles from "./css/Tabs.module.css";

interface TabProps<T extends string> {
	name: T;
	active: boolean;
	onClick: (e: React.MouseEvent<HTMLAnchorElement>, name: T) => void;
}

export function Tab<T extends string>({ name, active, onClick }: TabProps<T>) {
	// eslint-disable-next-line no-underscore-dangle
	const _onClick = React.useCallback((e: React.MouseEvent<HTMLAnchorElement>) => onClick(e, name), [onClick, name]);

	return (
		<a
			href={`#${name}`}
			className={cx(styles.tab, "simple-tab", { [styles.tabActive]: active, "simple-tab-active": active })}
			onClick={_onClick}
		>
			{name}
		</a>
	);
}

interface TabsProps {
	className?: string;
	small?: boolean;
}

export function Tabs({ children, className, small }: React.PropsWithChildren<TabsProps>) {
	return <nav className={cx(styles.tabs, className, { [styles.small]: small })}>{children}</nav>;
}

interface SimpleTabsProps<TabName extends string, Tabs extends readonly TabName[]> {
	tab: TabName;
	tabs: Tabs;
	setTab: (name: TabName) => void;
	className?: string;
	small?: boolean;
}

export default function SimpleTabs<TabName extends string, Tabs extends readonly TabName[]>({
	tab,
	tabs,
	setTab,
	className,
	small,
}: SimpleTabsProps<TabName, Tabs>) {
	const onClick = React.useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>, tab: TabName) => {
			e.preventDefault();
			setTab(tab);
		},
		[setTab],
	);

	return (
		<Tabs className={className} small={small}>
			{tabs.map(tabname => (
				<Tab key={tabname} name={tabname} active={tab === tabname} onClick={onClick} />
			))}
		</Tabs>
	);
}
